import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AuditEntry } from '@up2d8/contracts';
import {
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  CardFoot,
  DefinitionList,
  Drawer,
  Empty,
  FilterSelect,
  IconBtn,
  Input,
  PageHead,
  Table,
  type FilterOption,
} from '../admin-kit';
import { CardLoading, LoadError } from '../opvolging/parts';
import { relativeMoment } from '../opvolging/format';
import { formatDateTime } from '../../shared/format';

/** De filters die elk spoor kent; een spoor kan er eigen bij zetten (`extraQuery`). */
export interface AuditFilters {
  event?: string;
  actorId?: string;
  from?: string;
  to?: string;
}

/** Eén bladzijde van een spoor, zoals de API ze geeft. */
export interface AuditTrailPage<T extends AuditEntry> {
  items: T[];
  nextCursor: string | null;
}

/** Een extra kolom: kop, cel, en (optioneel) een regel in het detailpaneel. */
export interface AuditColumn<T extends AuditEntry> {
  key: string;
  label: string;
  cell: (entry: T) => ReactNode;
}

/**
 * Het scherm van een auditspoor (Stap 18, BHR-7; sinds AUD-1 voor twee sporen).
 *
 * Het spoor van een bedrijf (`/admin/audit`) en dat van het platform (`/master/audit`) tonen
 * hetzelfde: filters in de werkbalk, een dichte tabel, per regel een paneel met wat de regel bewaart.
 * Eén component en niet twee kopieën, zodat ze niet uit elkaar groeien; wat verschilt, komt binnen
 * als eigenschap: waar de regels vandaan komen, welke soorten er zijn, wie "wie" is, en het bedrijf
 * als extra filter en kolom op het platform.
 */
export function AuditTrail<T extends AuditEntry>({
  queryKey,
  fetchPage,
  title,
  sub,
  actions,
  kinds,
  actorTypes,
  renderActor,
  extraFilters,
  extraQuery = {},
  columns = [],
  emptyHint,
}: {
  /** Het begin van de querysleutel; de filters komen erachter. */
  queryKey: readonly unknown[];
  fetchPage: (
    query: AuditFilters & Record<string, string>,
    cursor: string | undefined,
  ) => Promise<AuditTrailPage<T>>;
  title: string;
  sub: string;
  /** Knoppen rechts in de kop, met de filters van nu (de export neemt ze mee). */
  actions?: (query: AuditFilters & Record<string, string>) => ReactNode;
  kinds: { value: string; label: string }[];
  /** Wie er in het filter "Wie" komt: de soorten actor met een naam. */
  actorTypes: AuditEntry['actorType'][];
  renderActor: (entry: T) => ReactNode;
  /** Extra filters in de werkbalk, na "Wie" (op het platform: het bedrijf). */
  extraFilters?: ReactNode;
  /** De waarden van die extra filters, voor de query. Leeg = geen filter. */
  extraQuery?: Record<string, string>;
  columns?: AuditColumn<T>[];
  emptyHint: string;
}) {
  const { t } = useTranslation();
  const [event, setEvent] = useState('');
  const [actorId, setActorId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [selected, setSelected] = useState<T | null>(null);
  // Wie er in het spoor voorkomt, onthouden over de filters heen: anders verdwijnt een naam uit
  // "Wie" zodra je op iemand anders filtert. De server heeft geen lijst van wie er handelde.
  const [actors, setActors] = useState<Map<string, string>>(() => new Map());

  const query: AuditFilters & Record<string, string> = {
    ...(event ? { event } : {}),
    ...(actorId ? { actorId } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...Object.fromEntries(Object.entries(extraQuery).filter(([, value]) => value)),
  };

  const entries = useInfiniteQuery({
    queryKey: [...queryKey, query],
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: AuditTrailPage<T>) => last.nextCursor ?? undefined,
    queryFn: ({ pageParam }) => fetchPage(query, pageParam),
  });
  const rows = (entries.data?.pages ?? []).flatMap((page) => page.items);

  useEffect(() => {
    const named = rows.filter((row) => actorTypes.includes(row.actorType) && row.actorId);
    if (named.every((row) => actors.has(row.actorId as string))) return;
    setActors((current) => {
      const next = new Map(current);
      for (const row of named) next.set(row.actorId as string, row.actorName);
      return next;
    });
  }, [rows, actors, actorTypes]);

  const actorOptions: FilterOption[] = [...actors.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <AdminPage>
      <PageHead title={title} sub={sub} actions={actions ? actions(query) : null} />

      <Card>
        <div className="a-toolbar">
          <FilterSelect
            label={t('audit.kind')}
            allLabel={t('audit.allKinds')}
            value={event}
            onChange={setEvent}
            testId="audit-kind"
            options={kinds}
          />
          <FilterSelect
            label={t('audit.who')}
            allLabel={t('beheer.opvolging.audit.everyone')}
            value={actorId}
            onChange={setActorId}
            testId="audit-actor"
            options={actorOptions}
          />
          {extraFilters}
          <label className="a-label" htmlFor="audit-from">
            {t('audit.from')}
          </label>
          <div style={{ width: 150 }}>
            <Input
              id="audit-from"
              type="date"
              value={from}
              max={to || undefined}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <label className="a-label" htmlFor="audit-to">
            {t('beheer.opvolging.audit.to')}
          </label>
          <div style={{ width: 150 }}>
            <Input
              id="audit-to"
              type="date"
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>

        {entries.isPending ? (
          <CardLoading rows={6} />
        ) : entries.isError ? (
          <LoadError onRetry={() => void entries.refetch()} error={entries.error} />
        ) : rows.length === 0 ? (
          <Empty
            icon={<AdminIcon name="history" size={26} />}
            title={t('audit.empty')}
            text={emptyHint}
          />
        ) : (
          <>
            <Table label={title}>
              <thead>
                <tr>
                  <th>{t('audit.when')}</th>
                  <th>{t('audit.who')}</th>
                  {columns.map((column) => (
                    <th key={column.key}>{column.label}</th>
                  ))}
                  <th>{t('audit.what')}</th>
                  <th className="act">
                    <span className="a-sr">{t('audit.detail')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    data-testid="audit-row"
                    className={row.id === selected?.id ? 'clickable sel' : 'clickable'}
                    onClick={() => setSelected(row)}
                  >
                    <td style={{ whiteSpace: 'nowrap' }} title={formatDateTime(row.occurredAt)}>
                      {relativeMoment(row.occurredAt)}
                    </td>
                    <td>{renderActor(row)}</td>
                    {columns.map((column) => (
                      <td key={column.key}>{column.cell(row)}</td>
                    ))}
                    <td>{row.summary}</td>
                    <td className="act">
                      <IconBtn
                        label={t('audit.detail')}
                        icon={<AdminIcon name="chevronRight" />}
                        onClick={(click) => {
                          click.stopPropagation();
                          setSelected(row);
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <CardFoot>
              <span>{t('beheer.opvolging.audit.shown', { count: rows.length })}</span>
              {entries.hasNextPage ? (
                <Btn
                  variant="sec"
                  size="sm"
                  icon={<AdminIcon name="chevronDown" />}
                  disabled={entries.isFetchingNextPage}
                  aria-busy={entries.isFetchingNextPage}
                  onClick={() => void entries.fetchNextPage()}
                >
                  {t('audit.more')}
                </Btn>
              ) : null}
            </CardFoot>
          </>
        )}
      </Card>

      {selected ? (
        <Drawer
          open
          onClose={() => setSelected(null)}
          title={selected.summary}
          sub={formatDateTime(selected.occurredAt)}
          testId="audit-detail"
        >
          <DefinitionList
            items={[
              [t('audit.when'), formatDateTime(selected.occurredAt)],
              [t('audit.who'), <span key="who">{renderActor(selected)}</span>],
              ...columns.map(
                (column) =>
                  [column.label, <span key={column.key}>{column.cell(selected)}</span>] as [
                    string,
                    ReactNode,
                  ],
              ),
              [t('audit.what'), selected.summary],
              [
                t('audit.object'),
                `${selected.objectType}${selected.objectId ? ` · ${selected.objectId}` : ''}`,
              ],
              [
                t('audit.event'),
                <span key="event" className="a-mono">
                  {selected.eventName}
                </span>,
              ],
              ...Object.entries(selected.details).map(
                ([key, value]) =>
                  [
                    key,
                    <span key={key} className="a-mono" style={{ overflowWrap: 'anywhere' }}>
                      {typeof value === 'string' ? value : JSON.stringify(value)}
                    </span>,
                  ] as [string, ReactNode],
              ),
            ]}
          />
        </Drawer>
      ) : null}
    </AdminPage>
  );
}

/** "Systeem" als badge: geen persoon, en zo staat het in elk spoor. */
export function SystemActor() {
  const { t } = useTranslation();
  return <Badge>{t('beheer.opvolging.audit.system')}</Badge>;
}
